// FORGE · AI Workflow Builder — subscriber-facing tab.
//
// Three sub-views selected via the top nav:
//   1. list    — active/paused workflows on this account + Deactivate
//   2. build   — conversational chat that plans + edits a draft, with
//                a live node graph + narrative + Activate button
//   3. draft   — resume editing an existing draft
//
// Phase 1 rollout: this tab renders only for users with role='admin'.
// The wrapping CommissionTracker mount block hides the tab entry
// entirely for non-admins.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';

async function forgeCall(action, body = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch('/api/forge', {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(jwt ? { authorization: `Bearer ${jwt}` } : {}),
    },
    body: JSON.stringify({ action, ...body }),
  });
  const j = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, body: j };
}

// ── Node graph ────────────────────────────────────────────────────

function NodeCard({ node, index }) {
  const kind = node.kind || node.type;
  const badge = KIND_BADGES[kind] || { label: kind, tone: 'bg-slate-100 text-slate-700' };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
      <div className="mb-1.5 flex items-center gap-2">
        <span className={`rounded px-1.5 py-0.5 text-[0.6rem] font-bold uppercase tracking-wider ${badge.tone}`}>{badge.label}</span>
        <code className="text-[0.65rem] text-slate-500">#{index + 1} · {node.id}</code>
      </div>
      {kind === 'send_sms_gated' && (
        <>
          <div className="text-xs text-slate-500 mb-1">SMS (gated)</div>
          <div className="rounded bg-slate-50 p-2 text-xs font-mono text-slate-800 whitespace-pre-wrap">{node.template || '(no template)'}</div>
        </>
      )}
      {kind === 'send_email' && (
        <>
          <div className="text-xs text-slate-500 mb-1">Email: <span className="font-semibold text-slate-700">{node.subject || '(no subject)'}</span></div>
          <div className="rounded bg-slate-50 p-2 text-xs font-mono text-slate-800 whitespace-pre-wrap">{node.template || '(no template)'}</div>
        </>
      )}
      {kind === 'queue_call_gated' && (
        <div className="text-xs text-slate-700">Queue call task: <span className="font-semibold">{node.title || '(no title)'}</span> → assignee: {node.assignee || node.assignee_id || 'owner'}</div>
      )}
      {kind === 'wait' && (
        <div className="text-xs text-slate-700">Wait <span className="font-semibold">{node.duration}</span> {node.quiet_hours_aware ? '(quiet-hours-aware)' : ''}</div>
      )}
      {kind === 'branch' && (
        <div className="text-xs text-slate-700">
          If <span className="font-mono">{node.condition || node.if}</span> — yes → <code>{node.yes || '?'}</code>, no → <code>{node.no || '?'}</code>
        </div>
      )}
      {kind === 'flag' && <div className="text-xs text-slate-700">Tag: <code>{node.tag}</code></div>}
      {kind === 'task' && <div className="text-xs text-slate-700">Task: <span className="font-semibold">{node.title}</span></div>}
      {kind === 'set_field' && <div className="text-xs text-slate-700">Set <code>{node.field}</code> = <code>{JSON.stringify(node.value)}</code></div>}
      {kind === 'invoke_ai_employee' && (
        <div className="text-xs text-slate-700">Hand off to <span className="font-bold uppercase">{node.employee}</span>: {node.task}</div>
      )}
      {kind === 'exit' && <div className="text-xs text-slate-700">Exit: <code>{node.exit_kind || 'EXIT_COMPLETED'}</code></div>}
    </div>
  );
}
const KIND_BADGES = {
  send_sms_gated:     { label: 'SMS',      tone: 'bg-blue-100 text-blue-900' },
  send_email:         { label: 'Email',    tone: 'bg-emerald-100 text-emerald-900' },
  queue_call_gated:   { label: 'Call',     tone: 'bg-purple-100 text-purple-900' },
  wait:               { label: 'Wait',     tone: 'bg-amber-100 text-amber-900' },
  wait_until:         { label: 'On date',  tone: 'bg-amber-100 text-amber-900' },
  branch:             { label: 'Branch',   tone: 'bg-slate-200 text-slate-800' },
  flag:               { label: 'Tag',      tone: 'bg-teal-100 text-teal-900' },
  task:               { label: 'Task',     tone: 'bg-orange-100 text-orange-900' },
  set_field:          { label: 'Set',      tone: 'bg-slate-100 text-slate-700' },
  invoke_ai_employee: { label: 'AI',       tone: 'bg-fuchsia-100 text-fuchsia-900' },
  exit:               { label: 'Exit',     tone: 'bg-red-100 text-red-900' },
};

function NodeGraph({ definition }) {
  if (!definition?.nodes?.length) return <div className="p-4 text-sm text-slate-400">(no nodes yet)</div>;
  const triggerLabel = definition.trigger?.type || '(no trigger)';
  const src = definition.trigger?.filters?.source || definition.trigger?.source;
  return (
    <div className="space-y-2">
      <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3">
        <div className="text-[0.6rem] font-bold uppercase tracking-wider text-slate-500">Trigger</div>
        <div className="text-sm font-semibold text-slate-800">{triggerLabel}{src ? ` · ${src}` : ''}</div>
      </div>
      {definition.nodes.map((n, i) => (
        <React.Fragment key={n.id || i}>
          <div className="flex justify-center">
            <div className="h-4 w-0.5 bg-slate-300" />
          </div>
          <NodeCard node={n} index={i} />
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Issues panel ──────────────────────────────────────────────────

function IssuesPanel({ issues }) {
  if (!issues?.length) return null;
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-3">
      <div className="mb-1.5 text-xs font-semibold text-red-900">Cannot activate — {issues.length} issue{issues.length === 1 ? '' : 's'}:</div>
      <ul className="space-y-0.5 text-xs text-red-900">
        {issues.map((it, i) => (
          <li key={i}>
            <code className="rounded bg-red-100 px-1">{it.code}</code> <span className="opacity-70">at {it.path}</span> — {it.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Chat panel ────────────────────────────────────────────────────

function ChatPanel({ transcript, onSubmit, busy, placeholder }) {
  const [input, setInput] = React.useState('');
  const submit = () => {
    if (!input.trim() || busy) return;
    onSubmit(input.trim());
    setInput('');
  };
  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 space-y-3 overflow-y-auto p-3">
        {(transcript || []).map((m, i) => (
          <div key={i} className={`rounded-lg px-3 py-2 text-sm ${m.role === 'user' ? 'bg-blue-100 text-blue-900 self-end ml-8' : 'bg-slate-100 text-slate-800 mr-8'}`}>
            <div className="text-[0.6rem] font-semibold uppercase tracking-wider opacity-60 mb-0.5">{m.role}</div>
            <div className="whitespace-pre-wrap">{m.content}</div>
          </div>
        ))}
        {busy && <div className="text-xs text-slate-400 italic">FORGE is thinking…</div>}
      </div>
      <div className="border-t border-slate-200 p-2">
        <div className="flex gap-2">
          <textarea
            className="flex-1 resize-none rounded border-slate-300 px-3 py-2 text-sm"
            rows={2}
            placeholder={placeholder || 'Describe the workflow you want to build…'}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(); }}
            disabled={busy}
          />
          <button
            type="button"
            onClick={submit}
            disabled={busy || !input.trim()}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {busy ? '…' : 'Send'}
          </button>
        </div>
        <div className="mt-1 text-[0.6rem] text-slate-400">Cmd/Ctrl+Enter to send</div>
      </div>
    </div>
  );
}

// ── Workflow list ─────────────────────────────────────────────────

function WorkflowList({ workflows, onSelect, onNew, onDeactivate, refreshing }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-slate-900">Your workflows</h2>
        <button
          type="button"
          onClick={onNew}
          className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700"
        >+ Build new workflow</button>
      </div>
      {workflows.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500">
          {refreshing ? 'Loading…' : 'No workflows yet. Click "Build new workflow" and tell FORGE what you want.'}
        </div>
      )}
      {workflows.map(w => (
        <div key={w.id} className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wider ${w.status === 'active' ? 'bg-emerald-100 text-emerald-900' : 'bg-slate-100 text-slate-600'}`}>{w.status}</span>
                <span className="font-semibold text-slate-800">{w.name}</span>
                {w.built_by_ai && <span className="rounded bg-fuchsia-100 px-1.5 py-0.5 text-[0.55rem] font-bold text-fuchsia-900">AI-BUILT</span>}
              </div>
              <div className="mt-1 text-xs text-slate-500">
                Trigger: <code>{w.definition?.trigger?.type || '?'}</code> · Vertical: {w.vertical} · Enrolled: {w.enrolled_count || 0}
              </div>
            </div>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => onSelect(w)}
                className="rounded bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-200"
              >View</button>
              {w.status === 'active' && (
                <button
                  type="button"
                  onClick={() => onDeactivate(w)}
                  className="rounded border border-red-200 bg-white px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-50"
                >Pause</button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Builder view (chat + graph side-by-side) ──────────────────────

function BuilderView({ initialSessionId, initialDraft, onActivated, onBack, showToast }) {
  const [sessionId,  setSessionId]  = React.useState(initialSessionId || null);
  const [transcript, setTranscript] = React.useState([]);
  const [draft,      setDraft]      = React.useState(initialDraft || null);
  const [busy,       setBusy]       = React.useState(false);
  const [sim,        setSim]        = React.useState(null);

  const submit = async (prompt) => {
    setTranscript(t => [...t, { role: 'user', content: prompt }]);
    setBusy(true);
    const isEdit = !!draft?.draft_id;
    const args = isEdit
      ? { action: 'edit', draft_id: draft.draft_id, instruction: prompt }
      : { action: 'plan', prompt, session_id: sessionId || undefined };
    const { ok, body } = await forgeCall(args.action, args);
    setBusy(false);
    if (!ok) {
      setTranscript(t => [...t, { role: 'assistant', content: `Error: ${body?.error || body?.detail || 'unknown'}` }]);
      return;
    }
    if (body.refused) {
      setTranscript(t => [...t, { role: 'assistant', content: body.reason || 'FORGE refused this shape.' }]);
      return;
    }
    if (body.session_id) setSessionId(body.session_id);
    if (body.draft_id) {
      setDraft({
        draft_id:  body.draft_id,
        definition: body.definition || body.after,
        issues:     body.issues || [],
        narrative:  body.narrative,
        diff:       body.diff,
      });
    }
    const summary = body.ok
      ? `Draft ready — ${body.definition?.nodes?.length || body.after?.nodes?.length || 0} steps.`
      : `Draft has ${body.issues?.length || 0} issue(s). See the panel on the right — fix them by asking FORGE to change something, or edit them yourself.`;
    setTranscript(t => [...t, { role: 'assistant', content: summary }]);
  };

  const simulate = async () => {
    if (!draft?.definition) return;
    setBusy(true);
    const { body } = await forgeCall('simulate', { definition: draft.definition });
    setBusy(false);
    setSim(body);
  };

  const activate = async () => {
    if (!draft?.draft_id) return;
    setBusy(true);
    const { ok, body } = await forgeCall('activate', { draft_id: draft.draft_id });
    setBusy(false);
    if (!ok) {
      showToast?.(`Activate failed: ${body?.error || 'unknown'}`);
      return;
    }
    showToast?.('Workflow activated ✓');
    onActivated?.(body.workflow_id);
  };

  const canActivate = draft && (!draft.issues || draft.issues.length === 0);

  return (
    <div className="grid h-[calc(100vh-11rem)] grid-cols-2 gap-4">
      {/* Left — chat */}
      <div className="flex flex-col rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 p-3">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-slate-900">Talk to FORGE</h2>
              <p className="text-xs text-slate-500">Describe the automation, or ask FORGE to tweak the current draft.</p>
            </div>
            <button type="button" onClick={onBack} className="text-xs font-semibold text-slate-500 hover:text-slate-900">← Back to list</button>
          </div>
        </div>
        <ChatPanel
          transcript={transcript}
          onSubmit={submit}
          busy={busy}
          placeholder={draft ? 'e.g. "Change the wait to 30 min" or "Only for Medicare leads"' : 'Describe the workflow — e.g. "When a Facebook lead arrives, email them, wait 48h, then text if they reply."'}
        />
      </div>

      {/* Right — draft */}
      <div className="flex flex-col rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-slate-200 p-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h2 className="text-base font-semibold text-slate-900">{draft?.definition?.name || 'Draft'}</h2>
              <div className="mt-0.5 text-xs text-slate-500">{draft ? `${draft.definition?.nodes?.length || 0} step${draft.definition?.nodes?.length === 1 ? '' : 's'}` : '—'}</div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={!draft || busy}
                onClick={simulate}
                className="rounded bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200 disabled:opacity-40"
              >Simulate</button>
              <button
                type="button"
                disabled={!canActivate || busy}
                onClick={activate}
                className="rounded bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-40"
              >Activate</button>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {!draft && <div className="text-sm text-slate-400 italic">Start by telling FORGE what you want on the left.</div>}
          {draft && (
            <>
              <IssuesPanel issues={draft.issues} />
              {draft.narrative && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="mb-1 text-[0.6rem] font-bold uppercase tracking-wider text-slate-500">Plain English</div>
                  <pre className="whitespace-pre-wrap text-xs text-slate-800">{draft.narrative}</pre>
                </div>
              )}
              {draft.diff && (
                <details className="rounded-lg border border-slate-200 bg-white p-3">
                  <summary className="cursor-pointer text-xs font-semibold text-slate-700">Diff from last version</summary>
                  <pre className="mt-2 whitespace-pre-wrap text-[0.65rem] font-mono text-slate-700">{draft.diff}</pre>
                </details>
              )}
              {sim && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                  <div className="mb-1 text-[0.6rem] font-bold uppercase tracking-wider text-blue-900">Dry-run trace</div>
                  <ul className="space-y-0.5 text-xs text-blue-900">
                    {(sim.trace || []).map((t, i) => (
                      <li key={i}><code>{t.node_id}</code> [{t.kind}] → <strong>{t.outcome}</strong> {t.note && <em className="opacity-70">— {t.note}</em>}</li>
                    ))}
                  </ul>
                </div>
              )}
              <NodeGraph definition={draft.definition} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main tab component ────────────────────────────────────────────

export default function ForgeView({ currentUser, showToast = () => {} }) {
  const [view,      setView]      = React.useState('list'); // 'list' | 'build' | 'detail'
  const [workflows, setWorkflows] = React.useState([]);
  const [refreshing,setRefreshing]= React.useState(true);
  const [selected,  setSelected]  = React.useState(null);

  const refresh = React.useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await supabase
        .from('forge_workflows')
        .select('id, owner_id, name, status, vertical, definition, built_by_ai, enrolled_count, created_at')
        .order('created_at', { ascending: false });
      setWorkflows(data || []);
    } catch (_e) { /* ignore */ }
    setRefreshing(false);
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  const startNew = () => { setSelected(null); setView('build'); };
  const backToList = async () => { setView('list'); await refresh(); };

  const deactivate = async (w) => {
    const { ok, body } = await forgeCall('deactivate', { workflow_id: w.id });
    if (!ok) return showToast(`Failed: ${body?.error || 'unknown'}`);
    showToast('Workflow paused.');
    await refresh();
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <header className="mb-6">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-bold text-slate-900">FORGE</h1>
          <span className="rounded-full bg-fuchsia-100 px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wider text-fuchsia-900">AI workflow builder</span>
        </div>
        <p className="mt-1 text-sm text-slate-500">
          Describe an automation in plain English. FORGE composes it from the platform's supported
          triggers, conditions, and actions — always through the consent + A2P + quiet-hours + DNC
          gates, so nothing you build can accidentally violate your compliance.
        </p>
      </header>

      {view === 'list' && (
        <WorkflowList
          workflows={workflows}
          refreshing={refreshing}
          onSelect={(w) => { setSelected(w); setView('detail'); }}
          onNew={startNew}
          onDeactivate={deactivate}
        />
      )}

      {view === 'build' && (
        <BuilderView
          initialSessionId={null}
          initialDraft={null}
          onBack={backToList}
          onActivated={backToList}
          showToast={showToast}
        />
      )}

      {view === 'detail' && selected && (
        <div>
          <button type="button" onClick={backToList} className="mb-3 text-xs font-semibold text-slate-500 hover:text-slate-900">← Back to list</button>
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">{selected.name}</h2>
                <div className="mt-0.5 text-xs text-slate-500">
                  {selected.status} · trigger: <code>{selected.definition?.trigger?.type}</code> · vertical: {selected.vertical}
                </div>
              </div>
              {selected.status === 'active' && (
                <button
                  type="button"
                  onClick={() => deactivate(selected).then(() => setView('list'))}
                  className="rounded border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
                >Pause workflow</button>
              )}
            </div>
            <NodeGraph definition={selected.definition} />
          </div>
        </div>
      )}
    </div>
  );
}
